"use client";

import React from "react";
import { InfoText } from "@/components/InfoText";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Calendar, Info } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useTranslation } from "react-i18next";

import { getFlatpickrLocale } from "@/utils/flatpickrLocale";
import { getEarliestStartOfInsurance, getTodayAtMidnight, getTomorrowAtMidnight } from "@/utils/funnel";
import { useFlatpickrField } from "@/hooks/useFlatpickrField";

/** Simpego only insures pets at least 8 weeks old — start of insurance can't be earlier
 * than that, per the business spec ("Mindestalter 8 Wochen"). */
export const MIN_PET_AGE_WEEKS = 8;

const atMidnight = (date: Date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

/** Earliest allowed start date: the general floor (tomorrow, but never before
 * EARLIEST_START_OF_INSURANCE), or (birthdate + minimum age) if that's later — the insurance can
 * never start today or in the past, regardless of the pet's age. */
export const computeEarliestStartOfInsurance = (birthDate: Date | undefined): Date => {
  const earliestDefault = getEarliestStartOfInsurance();
  if (!birthDate) return earliestDefault;
  const earliestFromAge = atMidnight(birthDate);
  earliestFromAge.setDate(earliestFromAge.getDate() + MIN_PET_AGE_WEEKS * 7);
  return earliestFromAge > earliestDefault ? earliestFromAge : earliestDefault;
};

/** Latest allowed start date: 12 months from today. */
export const getLatestStartOfInsurance = (): Date => {
  const maxDate = getTodayAtMidnight();
  maxDate.setFullYear(maxDate.getFullYear() + 1);
  return maxDate;
};

const formatDate = (date: Date) => date.toLocaleDateString("de-CH");

export const validateStartOfInsuranceDate = (
  date: Date | null | undefined,
  min: Date,
  t: (key: string, options?: Record<string, unknown>) => string
): string | null => {
  const maxDate = getLatestStartOfInsurance();
  const range = { min: formatDate(min), max: formatDate(maxDate) };
  if (!date) return t("priceCustomization.validation.startOfInsuranceRequired", range);
  const d = atMidnight(date);
  if (d < getTomorrowAtMidnight()) {
    return t("priceCustomization.validation.startOfInsuranceInPast", { ...range, date: formatDate(d) });
  }
  if (d < min) {
    return t("priceCustomization.validation.startOfInsuranceBeforeMinimum", { ...range, date: formatDate(d) });
  }
  if (d > maxDate) {
    return t("priceCustomization.validation.startOfInsuranceTooFar", { ...range, date: formatDate(d) });
  }
  return null;
};

interface StartOfInsuranceInputProps {
  value: Date;
  onChange: (date: Date) => void;
  min: Date;
  error: string | null;
  setError: (error: string | null) => void;
  isLoading: boolean;
  groupRef?: React.RefObject<HTMLDivElement>;
  labelText: string;
  hintText: string;
  infoTitle?: string;
  infoDescription?: string;
  inputId?: string;
}

const StartOfInsuranceInput: React.FC<StartOfInsuranceInputProps> = ({
  value,
  onChange,
  min,
  error,
  setError,
  isLoading,
  groupRef,
  labelText,
  hintText,
  infoTitle,
  infoDescription,
  inputId = "start-of-insurance",
}) => {
  const { t, i18n } = useTranslation();

  const flatpickrOptions = React.useMemo(() => ({
    dateFormat: "d.m.Y",
    altInput: true,
    altFormat: "d.m.Y",
    locale: getFlatpickrLocale(i18n.language),
    disableMobile: true,
    allowInput: true,
    clickOpens: false,
    minDate: min,
  }), [i18n.language, min]);

  const handleFlatpickrChange = (selectedDates: Date[]) => {
    if (selectedDates.length > 0) {
      const selectedAtMidnight = atMidnight(selectedDates[0]);
      onChange(selectedAtMidnight);
      setError(validateStartOfInsuranceDate(selectedAtMidnight, min, t));
    } else {
      setError(validateStartOfInsuranceDate(null, min, t));
    }
  };

  const { inputRef, instanceRef } = useFlatpickrField(flatpickrOptions, value, handleFlatpickrChange);

  // `onChange` (and thus `value`) only ever carries a fully-parsed date — Flatpickr doesn't
  // call it while the customer is mid-edit (e.g. right after clearing the field with
  // backspace), so `value` can keep silently holding the last valid date while the input
  // visibly shows something else. Callers must not treat a stale-but-valid `value` as proof the
  // field is fine — this flags the empty/unparseable state via `error` on blur so it survives
  // even though `value` itself didn't change. Callers should treat a non-null `error` as
  // authoritative (not re-derive it solely from `value`) so this can't be silently overwritten.
  const handleInputBlur = () => {
    const instance = instanceRef.current;
    if (instance && instance.selectedDates.length === 0) {
      setError(validateStartOfInsuranceDate(null, min, t));
    }
  };

  return (
    <div className="space-y-2 field-row" ref={groupRef}>
      <div className="flex items-center gap-2">
        <Label htmlFor={inputId}>
          {labelText}
        </Label>
        {infoTitle && infoDescription && (
          <Dialog>
            <DialogTrigger asChild>
              <Button variant="ghost" size="icon" className="h-6 w-6">
                <Info className="h-5! w-5! text-muted-foreground" />
                <span className="sr-only">{t("common.info")}</span>
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{infoTitle}</DialogTitle>
                <InfoText text={infoDescription} />
              </DialogHeader>
            </DialogContent>
          </Dialog>
        )}
      </div>
      <div className="relative">
        <Input
          id={inputId}
          // See the matching comment in BirthDateInput.tsx: React unconditionally re-asserts an
          // `<input>`'s `type` on every re-render of its fiber, which kept resetting Flatpickr's
          // `type="hidden"` mutation back to "text" — declaring it here too makes that forced
          // reassertion agree with Flatpickr instead of fighting it.
          type="hidden"
          placeholder={t("priceCustomization.startOfInsurancePlaceholder")}
          disabled={isLoading}
          className={cn("w-full pr-10", error && "border-destructive focus-visible:ring-destructive")}
          aria-invalid={Boolean(error)}
          ref={inputRef}
          onBlur={handleInputBlur}
          required
        />
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8"
          onClick={() => instanceRef.current?.open()}
          disabled={isLoading}
        >
          <Calendar className="h-4 w-4 text-gray-500" />
          <span className="sr-only">{t("common.openCalendar")}</span>
        </Button>
      </div>
      {error ? (
        <p className="text-sm text-destructive">{error}</p>
      ) : (
        <p className="text-xs text-muted-primary">{hintText}</p>
      )}
    </div>
  );
};

// `onChange` and `setError` are excluded: PetDetails passes fresh inline closures on every
// render that always delegate to the same stable useState setters, so treating them as
// "changed" would only force useFlatpickrField's value-sync effect to rerun redundantly
// (harmless, since it no-ops when the timestamp hasn't changed, but pointless).
//
// `value`/`min` are compared by timestamp, not reference, for the same reason: Flatpickr's own
// `allowInput` handling can settle on an equal-but-new Date instance without the calendar day
// actually changing.
const arePropsEqual = (prev: StartOfInsuranceInputProps, next: StartOfInsuranceInputProps) =>
  prev.value?.getTime() === next.value?.getTime() &&
  prev.min?.getTime() === next.min?.getTime() &&
  prev.error === next.error &&
  prev.isLoading === next.isLoading &&
  prev.groupRef === next.groupRef &&
  prev.labelText === next.labelText &&
  prev.hintText === next.hintText &&
  prev.infoTitle === next.infoTitle &&
  prev.infoDescription === next.infoDescription &&
  prev.inputId === next.inputId;

export default React.memo(StartOfInsuranceInput, arePropsEqual);
